import React, { useEffect, useMemo } from "react";
import {
  useTranslate,
  useGetIdentity,
  useCreate,
} from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Space,
  Button,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import {
  TunerRole,
  UserRole,
  SuperadminRole,
  ServiceRole,
} from "../../models/user";

type UserCreateModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const UserCreateModal: React.FC<UserCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const { data: currentUser } = useGetIdentity();
  const currentUserRoleId = (currentUser as any)?.role?.id;
  const isSuperAdmin =
    currentUserRoleId === SuperadminRole.id ||
    currentUserRoleId === String(SuperadminRole.id) ||
    Number(currentUserRoleId) === SuperadminRole.id;

  const [form] = Form.useForm();

  const { mutate: createUser, isLoading: isCreating } = useCreate();

  const selectedRoleId = Form.useWatch(["role", "id"], form);

  const { selectProps: accountSelectProps } = useSelect({
    resource: "accounts",
    optionLabel: "name",
    queryOptions: {
      enabled: selectedRoleId === UserRole.id,
    },
  });

  const { selectProps: roleSelectProps } = useSelect({
    resource: "roles",
    optionLabel: "name",
  });

  const filteredRoleOptions = useMemo(() => {
    if (!roleSelectProps.options) return [];
    let options = roleSelectProps.options;

    if (!isSuperAdmin) {
      options = options.filter((option: any) => {
        const roleId = option.value;
        return roleId !== SuperadminRole.id && roleId !== ServiceRole.id;
      });
    }

    return options.map((option: any) => {
      const roleName = option.label || option.name;
      const translationKey = `users.fields.roleValues.${roleName}`;
      return {
        ...option,
        label: _t(translationKey, roleName),
      };
    });
  }, [roleSelectProps.options, isSuperAdmin, _t]);

  const { selectProps: serviceProviderSelectProps } = useSelect({
    resource: "service-providers",
    optionLabel: "name",
    queryOptions: {
      enabled: selectedRoleId === TunerRole.id,
    },
  });

  const { selectProps: tenantSelectProps } = useSelect({
    resource: "tenants",
    optionLabel: "name",
    queryOptions: {
      enabled: isSuperAdmin,
    },
  });

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        provider: "email",
        status: { id: 1 },
      });
    } else {
      form.resetFields();
    }
  }, [open, form]);

  const handleFormValuesChange = (changedValues: any) => {
    if ("role" in changedValues && changedValues.role?.id !== undefined) {
      const roleId = changedValues.role.id;
      if (roleId !== TunerRole.id) {
        form.setFieldsValue({
          serviceProviderId: undefined,
        });
      }
      if (roleId !== UserRole.id) {
        form.setFieldsValue({
          account: undefined,
        });
      }
    }
  };

  const handleFinish = (values: any) => {
    createUser(
      {
        resource: "users",
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
        onError: (error: any) => {
          const errors = error?.response?.data?.errors;
          if (errors && "email" in errors) {
            if (errors["email"] === "emailAlreadyExists") {
              form.setFields([
                {
                  name: "email",
                  errors: [_t("users.errors.emailAlreadyExists")],
                },
              ]);
            }
          }
        },
      },
    );
  };

  return (
    <Modal
      title={_t("users.titles.create")}
      open={open}
      onCancel={onCancel}
      width={700}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isCreating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormValuesChange}
        onFinish={handleFinish}
        className="!mt-4"
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={_t("users.fields.email")}
              name={["email"]}
              rules={[{ required: true, message: _t("forms.errors.required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={_t("users.fields.password")}
              name={["password"]}
              rules={[
                { required: true, message: _t("forms.errors.required") },
                { min: 6, message: _t("fields.errors.minLength", { min: 6 }) },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={_t("users.fields.firstName")}
              name={["firstName"]}
              rules={[{ required: true, message: _t("forms.errors.required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={_t("users.fields.lastName")}
              name={["lastName"]}
              rules={[{ required: true, message: _t("forms.errors.required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={_t("users.fields.role")}
              name={["role", "id"]}
              rules={[{ required: true, message: _t("forms.errors.required") }]}
            >
              <Select {...roleSelectProps} options={filteredRoleOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            {selectedRoleId === UserRole.id && (
              <Form.Item
                label={_t("users.fields.account")}
                name={["account", "id"]}
                rules={[{ required: false }]}
              >
                <Select {...accountSelectProps} />
              </Form.Item>
            )}
            {selectedRoleId === TunerRole.id && (
              <Form.Item
                label={_t("users.fields.serviceProvider")}
                name="serviceProviderId"
                rules={[{ required: false }]}
              >
                <Select {...serviceProviderSelectProps} />
              </Form.Item>
            )}
          </Col>
        </Row>
        {isSuperAdmin && (
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={_t("users.fields.tenant")}
                name="tenantId"
                rules={[{ required: false }]}
              >
                <Select {...tenantSelectProps} />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Form.Item
          label={_t("users.fields.status")}
          name={["status", "id"]}
          hidden
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

